import styled from 'styled-components';
import { rgba } from 'polished';
import { ExclamationCircle } from '@/components/uielements/icon/exclamation-circle';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { COLORS, LAYOUT } from '@/constants/styles';

/**
 * Recovery UI for the store page — mounted by `pages/store/[...id].tsx`, the one
 * ordering surface without a tailored fallback (checkout and order-confirmation
 * own theirs). Order-scoped copy lives here, not in the app-wide `_app.tsx`
 * boundary, because that boundary also wraps pages where no order exists.
 *
 * The "pick up where you left off" promise is true: the basket persists to
 * sessionStorage on every mutation (`src/redux/basket/reducer.ts`) and
 * `_app.tsx` rehydrates it on load via `initializeSessionStorageBasket`
 * (`src/redux/basket/saga.ts`). Caveat: a deterministic bad-basket crash
 * repeats after reload — the same transient-crash assumption
 * `checkout-error-fallback.tsx` documents.
 */
export function OrderInterruptedFallback() {
  const handleReload = () => {
    // A full reload re-runs SSR + basket hydration (sessionStorage/server),
    // which resolves the transient state that triggered the crash.
    window.location.reload();
  };

  return (
    <StyledContainer role="alert" data-testid="order-interrupted-fallback">
      <StyledIconContainer>
        <StyledExclamationCircle title="" />
      </StyledIconContainer>
      <StyledTextContainer>
        <StyledTitle>Something interrupted your order</StyledTitle>
        <StyledMessage>Reload the page to pick up where you left off.</StyledMessage>
      </StyledTextContainer>
      <StyledReloadButton type="button" onClick={handleReload}>
        Reload page
      </StyledReloadButton>
    </StyledContainer>
  );
}

const StyledContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '24px',
  padding: `${LAYOUT.GUTTER * 2}px ${LAYOUT.GUTTER}px`,
  maxWidth: '480px',
  margin: '0 auto',
});

const StyledIconContainer = styled.div({
  width: '80px',
  height: '80px',
  borderRadius: '100%',
  backgroundColor: rgba(COLORS.PRIMARY_WARNING, 0.13),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${COLORS.PRIMARY_WARNING}`,
});

const StyledExclamationCircle = styled(ExclamationCircle)({
  width: '32px',
  height: '32px',
  color: COLORS.PRIMARY_WARNING,
});

const StyledTextContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
});

const StyledTitle = styled.span({
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '32px',
});

const StyledMessage = styled.span({
  fontSize: '16px',
  lineHeight: '24px',
});

const StyledReloadButton = styled(StyledPrimaryButton)({
  width: '100%',
  maxWidth: '320px',
});
